
import { Observable, forkJoin} from 'rxjs';
import {Component, OnInit, ViewChild, ElementRef} from '@angular/core';

import {
  AuthenticationService,
  BaseComponent,
  GenericISecResponse,
  Role,
  RoleListResponse,
  UserListResponse
} from 'iui-iconectiv-lib';
import { ServiceDeskService } from '../servicedesk/servicedesk.service';
import {ActivatedRoute} from "@angular/router";
import {UserProfile} from "iui-iconectiv-lib/lib/services/authentication.service";
import {UserFilter} from "./user-filter.model";
import { Table } from "primeng/table";
import {SelectItem, SortEvent} from "primeng/api";
// import {PaginatorModule} from 'primeng/paginator';

export class QueryPaging {
  constructor(public page: number, public limit: number) {
  }
}

export class SortCriteria {
  constructor(public column: string, public descending: boolean) {
  }
}

@Component({
  selector: 'user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent extends BaseComponent implements OnInit {

  @ViewChild('ptable') ptable: Table;
  @ViewChild('psortIcon') psortIcon: ElementRef;

  userHeaders: any[];
  users: UserProfile[];
  roles: Role[];
  roleNames: SelectItem[];
  filter: UserFilter = new UserFilter();
  accountId: string;
  paging = new QueryPaging(0, 8000);
  sortCriteria = new SortCriteria('username', false);
  totalItems: number;

  constructor(private route: ActivatedRoute,
              private authService: AuthenticationService,
              private serviceDeskService: ServiceDeskService) {
    super();

    this.userHeaders = [
      {field: 'username', header: 'User ID', width: '7'},
      {field: 'accountId', header: 'Account ID', width: '7'},
      {field: 'firstname', header: 'First Name', width: '10'},
      {field: 'lastname', header: 'Last Name', width: '10'},
      {field: 'email', header: 'Email Address', width: '16'},
      {field: 'phone', header: 'Telephone', width: '9'},
      {field: 'userRoleNames', header: 'User Role', width: '7'},
      {field: 'createdtime', header: 'Create Date/Time', width: '13'},
      {field: 'lastLoginDateTime', header: 'Last Login Date/Time', width: '13'},
      {field: 'userRegistrationTokenExpTime', header: 'Password/Token Expiration', width: '13'},
      {field: 'disabled', header: 'Allow Access', width: '8'},
      {field: 'send', header: 'Send', width: '6'},
      {field: 'locked', header: 'Unlock', width: '7'},
    ];
  }


  ngOnInit(): void {
    const authorizationToken = this.authService.getToken();
    this.accountId = this.route.snapshot.params['accountId'];
    if (this.accountId) {
      this.filter.accountId = this.accountId;
    }
    this.initUsersAndRoles();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.ptable.filter(this.accountId, 'accountId', 'contains');

    }, 0);
  }

  initUsersAndRoles() {
    let obs: Observable<GenericISecResponse>[] = [];
    obs.push(this.authService.getAllRoles());
    // if an accountId was passed in the parameter, send api request with id to retrieve only users associated with that account
    if (this.accountId) {
      obs.push(this.serviceDeskService.getUsersWithId(this.accountId, this.paging.limit, this.sortCriteria.column, this.sortCriteria.descending ? 'DESC' : 'ASC', this.paging.page));
    }
    else {
      obs.push(this.authService.getAllUsers(this.paging.limit, this.sortCriteria.column, this.sortCriteria.descending ? 'DESC' : 'ASC', this.paging.page));
    }

    forkJoin(obs).subscribe(results => {
        results.forEach(rsp => {
          if (rsp.responseType != 'rolelist') {

            const thisuser = this.authService.getUser();
            const ulr = rsp as UserListResponse;
            this.users = (<UserListResponse>rsp).data;
            this.totalItems = ulr.total;
          
            if (this.users) {
              this.users.forEach(u => {
                const now = new Date().getTime();
                if (thisuser && u.username === thisuser.username) {
                  u.self = true;
                }
                if (u.forgotPasswordTokenExpirationTime != null) {
                  u.forgotPasswordTokenExpirationTime = new Date(u.forgotPasswordTokenExpirationTime.toString());
                }

                if ((u.forgotPasswordTokenExpirationTime) &&
                  (now >= u.forgotPasswordTokenExpirationTime.getTime())) {
                  delete u.forgotPasswordTokenExpirationTime;
                }
                u.allowAccess = !u.disabled;
              });
            }
          }
          else if (rsp.responseType === 'rolelist') {
            this.roles = (<RoleListResponse>rsp).data.filter(r => r.active);
            this.roleNames = this.roles.map(role => <SelectItem>{label: role.rolename, value: role.rolename});
          }
        });
      },
      error => {
        this.processError(error);
      },
      () => {
        if (this.users) {
          this.users.forEach(u => {
            let userRoleNames = [];
            u.userRoleList.forEach(r => {
              const rr = this.roles.find(rr => rr.roleid === r.roleid);
              if (rr) {
                r.active = rr.active;
                r.description = rr.description;
                r.grantable = rr.grantable;
                r.rolename = rr.rolename;
                userRoleNames.push(rr.rolename);
              }
            })
            u['userRoleNames'] = userRoleNames.join(', ');
          })
        }
      });
  }

  paginate(event: any) {
    if (this.paging.page !== event.page) {
      this.paging.page = event.page;
      this.initUsersAndRoles();
    }
  }

  customSort(event) {
    // console.log(event);
  }

  toggleAccess(u: UserProfile) {
    u.disabled = !u.allowAccess;
    this.authService.updateUserStatus(u.username, u.disabled).subscribe(rsp => {
      if (rsp.status === 'FAIL') {
        this.displayErrorMessage(rsp.message);
      }
    });
  }

  initPasswordReset(u: UserProfile) {
    this.authService.forgotpassword(u.username).subscribe(rsp => {
        if (rsp.status === 'FAIL') {
          this.displayErrorMessage(rsp.message, rsp.title);
        }
        else {
          this.initUsersAndRoles();
        }
      },
      error => {
        this.processHttpError(error);
      });
  }

  unlock(u: UserProfile) {
    this.authService.unlockUser(u.username).subscribe(rsp => {
        if (rsp.status === 'FAIL') {
          this.displayErrorMessage(rsp.message, rsp.title);
        }
        else {
          this.initUsersAndRoles();
        }
      },
      error => {
        this.processHttpError(error);
      });
  }

  resendWelcome(u: UserProfile) {
    this.authService.resendWelcome(u.email).subscribe(rsp => {
        if (rsp.status === 'FAIL') {
          this.displayErrorMessage(rsp.message, rsp.title);
        }
      },
      error => {
        this.processHttpError(error);
      });
  }

  onFilter(event) {
    if (!event.filters['accountId'] && this.accountId != undefined) {
      this.accountId = undefined;
      this.initUsersAndRoles();
    }
  }

  sortBy(event, descending = true) {

    if (event.field === 'userRoleNames' && this.sortCriteria.column === 'userRoleList.roleid') {
      this.sortCriteria.descending = !this.sortCriteria.descending;
      this.initUsersAndRoles();
      return;
    } 
    if (event.field === 'userRoleNames' && this.sortCriteria.column !== 'userRoleList.roleid') this.sortCriteria.column = 'userRoleList.roleid';
    else this.sortCriteria.column = event.field;

    if (event.field === this.sortCriteria.column) {
      this.sortCriteria.descending = !this.sortCriteria.descending
    }
    else {
      this.sortCriteria.descending = descending;
      
    }
    this.initUsersAndRoles();

  }


}
